/**
 * /p/satisfacao/:token — a pesquisa que o cliente responde (A27).
 *
 * O modelo de NPS e o KPI no Cliente 360 existiam desde antes; nada
 * escrevia neles. Esta é a ponta que faltava: o link que chega por
 * e-mail depois da entrega, sem login e sem cadastro.
 *
 * Uma pergunta só, botões grandes — quem responde está no celular, de
 * pé na obra, e a chance de resposta cai a cada campo a mais.
 *
 * Padrão reusado de /p/entrega/:token.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { ACCESS_API_URL } from "../../config/api";

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f4f4f5;
  color: #18181b;
  font-family: system-ui, -apple-system, sans-serif;
`;

const Header = styled.header`
  padding: 24px 16px;
  background: #fff;
  border-bottom: 1px solid #e4e4e7;
  text-align: center;
`;

const Main = styled.main`
  flex: 1;
  max-width: 640px;
  margin: 0 auto;
  padding: 32px 16px;
  width: 100%;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const Escala = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin: 20px 0 8px;
  @media (min-width: 480px) {
    grid-template-columns: repeat(11, 1fr);
  }
`;

/* Cor comunica a faixa (detrator/neutro/promotor), não decora — a mesma
   régua que o gestor vê do outro lado. */
const NotaBotao = styled.button`
  aspect-ratio: 1;
  min-height: 44px;
  border-radius: 6px;
  border: 2px solid ${({ $ativo, $tone }) => ($ativo ? $tone : "#d4d4d8")};
  background: ${({ $ativo, $tone }) => ($ativo ? $tone : "#fff")};
  color: ${({ $ativo }) => ($ativo ? "#fff" : "#3f3f46")};
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LegendaEscala = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #71717a;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 12px;
  border: 1px solid #d4d4d8;
  border-radius: 6px;
  font-size: 16px;
  font-family: inherit;
  box-sizing: border-box;
  margin-top: 16px;
`;

const Button = styled.button`
  padding: 14px 24px;
  background: ${({ $primary }) => ($primary ? "#C2683A" : "#e4e4e7")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#18181b")};
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 16px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusBox = styled.div`
  padding: 16px;
  border-radius: 6px;
  background: ${({ $tone }) =>
    $tone === "success" ? "#d1fae5" : $tone === "danger" ? "#fee2e2" : "#e0f2fe"};
  color: ${({ $tone }) =>
    $tone === "success" ? "#065f46" : $tone === "danger" ? "#991b1b" : "#075985"};
  font-weight: 600;
  margin-bottom: 16px;
  text-align: center;
`;

const API_URL = ACCESS_API_URL;

function toneDaNota(nota) {
  if (nota >= 9) return "#16a34a";
  if (nota >= 7) return "#ca8a04";
  return "#dc2626";
}

export default function SatisfacaoPublica() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);
  const [nota, setNota] = useState(null);
  const [comentario, setComentario] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/publico/satisfacao/${token}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detalhe || `Erro ${res.status}`);
      }
      setDados(await res.json());
    } catch (err) {
      setError(err.message || "Não foi possível abrir a pesquisa.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const enviar = async () => {
    if (nota == null) return;
    setEnviando(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/publico/satisfacao/${token}/responder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nota, comentario }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detalhe || `Erro ${res.status}`);
      }
      setSucesso(true);
    } catch (err) {
      setError(err.message || "Não foi possível enviar sua resposta.");
    } finally {
      setEnviando(false);
    }
  };

  const cabecalho = (
    <Header>
      <h1 style={{ margin: 0, fontSize: 24 }}>PharuERP</h1>
    </Header>
  );

  if (loading) {
    return (
      <Container>
        {cabecalho}
        <Main>
          <Card>Carregando…</Card>
        </Main>
      </Container>
    );
  }

  if (!dados) {
    return (
      <Container>
        {cabecalho}
        <Main>
          <Card>
            <StatusBox $tone="danger">
              {error || "Esta pesquisa não está disponível."}
            </StatusBox>
            <p style={{ margin: 0, color: "#52525b" }}>
              Se você recebeu este link por e-mail, ele pode ter vencido. Pode
              responder ao próprio e-mail contando como foi — a gente lê.
            </p>
          </Card>
        </Main>
      </Container>
    );
  }

  if (sucesso || dados.ja_respondida) {
    return (
      <Container>
        {cabecalho}
        <Main>
          <Card>
            <StatusBox $tone="success">Obrigado! Resposta registrada.</StatusBox>
            <p style={{ margin: 0, color: "#52525b" }}>
              {sucesso
                ? "Sua opinião chega direto a quem cuidou do seu projeto."
                : "Esta pesquisa já tinha sido respondida."}
            </p>
          </Card>
        </Main>
      </Container>
    );
  }

  if (!dados.pode_responder) {
    return (
      <Container>
        {cabecalho}
        <Main>
          <Card>
            <StatusBox $tone="danger">O prazo desta pesquisa venceu.</StatusBox>
          </Card>
        </Main>
      </Container>
    );
  }

  return (
    <Container>
      {cabecalho}
      <Main>
        <Card>
          <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>
            {dados.cliente_nome ? `${dados.cliente_nome}, ` : ""}como foi a sua
            experiência com a gente?
          </h2>
          <p style={{ margin: 0, color: "#52525b", fontSize: 14 }}>
            De 0 a 10, o quanto você indicaria nosso trabalho a um amigo?
            {dados.pedido_numero ? ` (pedido ${dados.pedido_numero})` : ""}
          </p>

          <Escala role="radiogroup" aria-label="Nota de 0 a 10">
            {Array.from({ length: 11 }, (_, n) => (
              <NotaBotao
                key={n}
                type="button"
                role="radio"
                aria-checked={nota === n}
                aria-label={`Nota ${n}`}
                $ativo={nota === n}
                $tone={toneDaNota(n)}
                disabled={enviando}
                onClick={() => setNota(n)}
              >
                {n}
              </NotaBotao>
            ))}
          </Escala>
          <LegendaEscala>
            <span>Não indicaria</span>
            <span>Indicaria com certeza</span>
          </LegendaEscala>

          <Textarea
            placeholder="Quer contar mais alguma coisa? (opcional)"
            value={comentario}
            maxLength={2000}
            disabled={enviando}
            onChange={(e) => setComentario(e.target.value)}
          />

          {error ? <StatusBox $tone="danger">{error}</StatusBox> : null}

          <Button $primary disabled={nota == null || enviando} onClick={enviar}>
            {enviando ? "Enviando…" : "Enviar resposta"}
          </Button>
        </Card>
      </Main>
    </Container>
  );
}
